/**
 * Fase 2 (W4, A2): fonte do texto e escala. As prévias são OFL-1.1, servidas pelo próprio app (`@fontsource-variable`):
 * só o endereço do arquivo latino entra no bundle; o arquivo só é baixado quando a fonte é escolhida ou mostrada na
 * prévia (`FontFace.load`). A fonte enviada pelo admin vem da rota cacheável pelo hash. O nome de família registrado é
 * sempre interno (`malgoos-...`), nunca o do arquivo, e o registro é por `FontFace` (nada de CSS montado com texto).
 * Números e valores continuam na mono (`.numero`), então só o texto corrido muda.
 *
 * `public/tema.js` lê o que fica guardado aqui (`malgoos.fonte`, `malgoos.escala`, `malgoos.favicon`) e aplica antes do
 * primeiro paint.
 */

export type ChaveFonte = 'hanken' | 'bricolage' | 'inter' | 'atkinson' | 'source_sans' | 'plex_sans' | 'lexend' | 'sistema' | 'roboto' | 'dm_sans';
export type EscalaTexto = 100 | 110 | 125 | 140 | 150;

export interface FontePrevia {
  chave: ChaveFonte;
  /** Nome exibido (nome próprio da fonte; o da do sistema vem do i18n). */
  nome: string;
  familia: string;
  url: string | null;
}

const caminho = (u: URL) => u.pathname;

export const FONTES_PREVIAS: FontePrevia[] = [
  // Lote 19 (design system da Malgo, BrandBook v1.3): a Hanken Grotesk é a do produto (substituta web da Articulat CF, de
  // licença pendente) e já vem carregada pelo main.tsx (@fontsource-variable/hanken-grotesk). A Bricolage, a de antes, virou
  // opção como as outras.
  { chave: 'hanken', nome: 'Hanken Grotesk', familia: 'Hanken Grotesk Variable', url: null },
  {
    chave: 'bricolage',
    nome: 'Bricolage Grotesque',
    familia: 'malgoos-bricolage',
    url: caminho(new URL('../../node_modules/@fontsource-variable/bricolage-grotesque/files/bricolage-grotesque-latin-wght-normal.woff2', import.meta.url)),
  },
  {
    chave: 'inter',
    nome: 'Inter',
    familia: 'malgoos-inter',
    url: caminho(new URL('../../node_modules/@fontsource-variable/inter/files/inter-latin-wght-normal.woff2', import.meta.url)),
  },
  {
    chave: 'atkinson',
    nome: 'Atkinson Hyperlegible',
    familia: 'malgoos-atkinson',
    url: caminho(new URL('../../node_modules/@fontsource-variable/atkinson-hyperlegible-next/files/atkinson-hyperlegible-next-latin-wght-normal.woff2', import.meta.url)),
  },
  {
    chave: 'source_sans',
    nome: 'Source Sans 3',
    familia: 'malgoos-source-sans',
    url: caminho(new URL('../../node_modules/@fontsource-variable/source-sans-3/files/source-sans-3-latin-wght-normal.woff2', import.meta.url)),
  },
  {
    chave: 'plex_sans',
    nome: 'IBM Plex Sans',
    familia: 'malgoos-plex-sans',
    url: caminho(new URL('../../node_modules/@fontsource-variable/ibm-plex-sans/files/ibm-plex-sans-latin-wght-normal.woff2', import.meta.url)),
  },
  {
    chave: 'lexend',
    nome: 'Lexend',
    familia: 'malgoos-lexend',
    url: caminho(new URL('../../node_modules/@fontsource-variable/lexend/files/lexend-latin-wght-normal.woff2', import.meta.url)),
  },
  { chave: 'sistema', nome: '', familia: 'system-ui', url: null },
];

/**
 * Estilos de interface (27/10): fontes que só entram pelo estilo escolhido (nucleo/estilos.ts), fora da lista da fonte
 * pessoal. Roboto e DM Sans são OFL-1.1, servidas pelo app como as prévias (o arquivo só baixa quando o estilo é usado).
 */
export const FONTES_DE_ESTILO: FontePrevia[] = [
  {
    chave: 'roboto',
    nome: 'Roboto',
    familia: 'malgoos-roboto',
    url: caminho(new URL('../../node_modules/@fontsource-variable/roboto/files/roboto-latin-wght-normal.woff2', import.meta.url)),
  },
  {
    chave: 'dm_sans',
    nome: 'DM Sans',
    familia: 'malgoos-dm-sans',
    url: caminho(new URL('../../node_modules/@fontsource-variable/dm-sans/files/dm-sans-latin-wght-normal.woff2', import.meta.url)),
  },
];

export const ESCALAS: EscalaTexto[] = [100, 110, 125, 140, 150];

const RE_ARQUIVO = /^arquivo:([0-9a-f]{64})$/;

export const urlArquivoAparencia = (sha256: string) => `/api/v1/configuracoes/aparencia/arquivo/${sha256}`;
export const familiaEnviada = (sha256: string) => `malgoos-ws-${sha256.slice(0, 12)}`;

export interface FonteResolvida {
  familia: string;
  url: string | null;
}

/** Chave gravada (prévia ou `arquivo:<sha256>`) para família e endereço; desconhecida volta à Hanken. */
export function resolverFonte(chave: string | null | undefined): FonteResolvida {
  const m = chave ? RE_ARQUIVO.exec(chave) : null;
  if (m) return { familia: familiaEnviada(m[1]!), url: urlArquivoAparencia(m[1]!) };
  const p = [...FONTES_PREVIAS, ...FONTES_DE_ESTILO].find((f) => f.chave === chave) ?? FONTES_PREVIAS[0]!;
  return { familia: p.familia, url: p.url };
}

/**
 * Precedência: a pessoa (se o admin permitir) > a do estilo de interface escolhido (fora da Malgo) > o workspace > Hanken.
 * O estilo é escolha da própria pessoa, então vale sobre a fonte do workspace; a fonte pessoal continua mandando.
 */
export function fonteEfetiva(aparencia: { fonte?: string | null; permitir_fonte_pessoal?: boolean } | null | undefined, pessoal: string | null | undefined, doEstilo?: string | null): string {
  if (pessoal && aparencia?.permitir_fonte_pessoal !== false) return pessoal;
  if (doEstilo) return doEstilo;
  return aparencia?.fonte || 'hanken';
}

/** Valor da variável `--fonte-texto`: a família entre aspas (nome interno, sem aspas dentro) ou `system-ui`. */
export function valorCssFamilia(familia: string): string {
  if (familia === 'system-ui') return 'system-ui';
  return /^[A-Za-z0-9 _-]{1,64}$/.test(familia) ? `'${familia}'` : "'Hanken Grotesk Variable'";
}

/** Pilha completa para a prévia (estilo inline): a família e a reserva do produto, se o arquivo faltar ou não cobrir o texto. */
export const pilhaFonte = (familia: string) => `${valorCssFamilia(familia)}, 'Hanken Grotesk Variable', system-ui, sans-serif`;

const registradas = new Map<string, FontFace>();

/** Registra a família por `FontFace` (uma vez) e começa a baixar; falha de rede deixa a pilha de reserva em uso. */
export function registrarFonte(f: FonteResolvida): Promise<unknown> {
  if (!f.url || typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) return Promise.resolve();
  let face = registradas.get(f.familia);
  if (!face) {
    face = new FontFace(f.familia, `url(${f.url})`, { weight: '100 900', display: 'swap' });
    document.fonts.add(face);
    registradas.set(f.familia, face);
  }
  return face.load().catch(() => undefined);
}

function guardar(chave: string, valor: string | null) {
  try {
    if (valor === null) localStorage.removeItem(chave);
    else localStorage.setItem(chave, valor);
  } catch {
    /* sem storage */
  }
}

/** Aplica a fonte e a escala na página e guarda para o `tema.js` aplicar antes do paint na próxima carga. */
export function aplicarFonte(chave: string | null | undefined, escala: number | null | undefined) {
  const f = resolverFonte(chave);
  void registrarFonte(f);
  const raiz = document.documentElement;
  raiz.style.setProperty('--fonte-texto', valorCssFamilia(f.familia));
  const e = escala === 110 || escala === 125 || escala === 140 || escala === 150 ? String(escala) : null;
  if (e) raiz.dataset.escala = e;
  else delete raiz.dataset.escala;
  guardar('malgoos.fonte', f.familia === FONTES_PREVIAS[0]!.familia ? null : JSON.stringify(f));
  guardar('malgoos.escala', e);
}

/** Favicon da marca (logo enviada) ou o do produto; guardado para o `tema.js`. */
export function aplicarFavicon(logoSha: string | null | undefined) {
  const url = logoSha && /^[0-9a-f]{64}$/.test(logoSha) ? urlArquivoAparencia(logoSha) : null;
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) {
    // O navegador guarda o favicon pelo endereço: trocou o arquivo, troca o `v` aqui e no index.html (03/11).
    link.href = url ?? '/favicon.svg?v=20261103b';
    if (url) link.removeAttribute('type');
    else link.type = 'image/svg+xml';
  }
  guardar('malgoos.favicon', url);
}
